import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  LogOut, Users, MessageSquare, Phone, Shield, Download, Eye, ArrowLeft, 
  Menu, Send, TrendingUp, Activity, ChevronRight, X, Video, Play, Clock,
  Timer, AlertTriangle, Settings, User, Mail, PhoneCall, Calendar, MapPin, ExternalLink, Copy, Lock, Key, Smartphone, Trash2, Loader2
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to add admin token to media URLs for secure access
const getSecureMediaUrl = (url) => {
  if (!url) return null;
  // Try both token keys for backwards compatibility
  const adminToken = localStorage.getItem('admin_token') || localStorage.getItem('adminToken');
  const baseUrl = url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}token=${adminToken}`;
};

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteAnalytics, setInviteAnalytics] = useState(null);
  const [inviteFilter, setInviteFilter] = useState('all');
  const [inviteSearch, setInviteSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  
  const [chatsLoading, setChatsLoading] = useState(false);

  // Chat view states
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatData, setChatData] = useState(null);
  
  // Security settings states
  const [securitySettings, setSecuritySettings] = useState(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [totpSecret, setTotpSecret] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  
  // IP Location states
  const [showIPModal, setShowIPModal] = useState(false);
  const [selectedIP, setSelectedIP] = useState(null);
  const [ipLocation, setIPLocation] = useState(null);
  const [ipLoading, setIPLoading] = useState(false);
  
  // User Profile states
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProfileData, setUserProfileData] = useState(null);
  const [userProfileLoading, setUserProfileLoading] = useState(false);
  
  // Recording playback states
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [recordingError, setRecordingError] = useState(false);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  
  // Delete user states
  const [showDeleteUserConfirm, setShowDeleteUserConfirm] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  
  // SMS Dialog states
  const [showSMSDialog, setShowSMSDialog] = useState(false);
  const [smsTargetUser, setSmsTargetUser] = useState(null);
  const [smsMessage, setSmsMessage] = useState('');
  const [sendingSMS, setSendingSMS] = useState(false);
  const [showSMSConfirm, setShowSMSConfirm] = useState(false);
  
  // Default SMS template
  const DEFAULT_SMS_TEMPLATE = `Your Privluxe profile has been approved.

You can now start private, discreet conversations. Send your first invite to someone you trust and connect on your terms — without revealing your identity.

Begin now: https://privluxe.com

– Team Privluxe`;
  
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllData();
    
    // Admin dashboard handles its own scrolling
    // No need to set overflow since global CSS now allows scroll
  }, []);

  useEffect(() => {
    if (activeTab === 'chats') {
      fetchConversations();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'invites') fetchInvites();
  }, [activeTab, inviteFilter, inviteSearch]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatData]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/stats`, { headers })
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async () => {
    setChatsLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/conversations/list`, { headers });
      if (res.data && Array.isArray(res.data)) {
        setConversations(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      toast.error('Failed to fetch conversations');
    } finally {
      setChatsLoading(false);
    }
  };

  const fetchChatDetail = async (chatId) => {
    setChatLoading(true);
    setSelectedChat(chatId);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/conversations/${chatId}/full`, { headers });
      setChatData(res.data);
    } catch (error) {
      console.error('Failed to load chat:', error);
      toast.error('Failed to load chat');
      setChatData(null);
    } finally {
      setChatLoading(false);
    }
  };

  const fetchInvites = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      let url = `${API}/admin/invites`;
      const params = new URLSearchParams();
      if (inviteFilter !== 'all') params.append('status', inviteFilter);
      if (inviteSearch) params.append('search', inviteSearch);
      if (params.toString()) url += `?${params.toString()}`;
      
      console.log('[Admin] Fetching invites from:', url);
      
      const [invitesRes, analyticsRes] = await Promise.all([
        axios.get(url, { headers }),
        axios.get(`${API}/admin/invites/analytics`, { headers })
      ]);
      
      console.log('[Admin] Invites response:', invitesRes.data?.length || 0, 'invites');
      console.log('[Admin] Analytics response:', analyticsRes.data?.total_invites || 0, 'total');
      
      setInvites(invitesRes.data || []);
      setInviteAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('[Admin] Failed to fetch invites:', error);
    }
  };

  // Download invites as Excel
  const downloadInvitesExcel = async () => {
    setDownloadingExcel(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/invites/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'privluxe_invites.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invite data downloaded!');
    } catch (error) {
      console.error('Failed to download invites:', error);
      toast.error('Failed to download invite data');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    toast.success('Logged out');
    navigate('/admin');
  };

  // Security functions
  const fetchSecuritySettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSecuritySettings(response.data);
    } catch (error) {
      console.error('Failed to fetch security settings:', error);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password changed successfully');
      setShowPasswordChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      fetchSecuritySettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const setup2FA = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/2fa/setup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTotpSecret(response.data.secret);
      setQrCodeUrl(`${API}/admin/2fa/qr-code?token=${token}`);
      setShow2FASetup(true);
    } catch (error) {
      toast.error('Failed to setup 2FA');
    }
  };

  const enable2FA = async () => {
    if (!totpCode || totpCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/2fa/enable`, {
        totp_code: totpCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('2FA enabled successfully!');
      setShow2FASetup(false);
      setTotpCode('');
      setTotpSecret(null);
      setQrCodeUrl(null);
      fetchSecuritySettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    }
  };

  const disable2FA = async () => {
    if (!totpCode || totpCode.length !== 6) {
      toast.error('Please enter your current 2FA code to disable');
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/2fa/disable`, {
        totp_code: totpCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('2FA disabled');
      setTotpCode('');
      fetchSecuritySettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    }
  };

  // Fetch security settings when security tab is active
  useEffect(() => {
    if (activeTab === 'security') {
      fetchSecuritySettings();
    }
  }, [activeTab]);

  // IP Location lookup
  const handleIPClick = async (ip) => {
    if (!ip || ip === '-') return;
    
    // Handle multiple IPs - take the first one (most likely the real client IP)
    const singleIP = ip.split(',')[0].trim();
    
    setSelectedIP(ip);
    setShowIPModal(true);
    setIPLoading(true);
    setIPLocation(null);
    
    // Check if it's a private IP
    const isPrivateIP = (ipAddr) => {
      const parts = ipAddr.split('.');
      if (parts.length !== 4) return false;
      const first = parseInt(parts[0]);
      const second = parseInt(parts[1]);
      // Check for private IP ranges
      if (first === 10) return true;
      if (first === 172 && second >= 16 && second <= 31) return true;
      if (first === 192 && second === 168) return true;
      if (first === 127) return true;
      return false;
    };
    
    if (isPrivateIP(singleIP)) {
      setIPLocation({ error: 'Private IP address - cannot determine location' });
      setIPLoading(false);
      return;
    }
    
    try {
      // Using ipapi.co (free HTTPS support, 1000 requests/day free)
      const response = await axios.get(`https://ipapi.co/${singleIP}/json/`);
      if (response.data && !response.data.error) {
        setIPLocation({
          city: response.data.city,
          regionName: response.data.region,
          country: response.data.country_name,
          countryCode: response.data.country_code,
          lat: response.data.latitude,
          lon: response.data.longitude,
          timezone: response.data.timezone,
          isp: response.data.org,
          org: response.data.org,
          zip: response.data.postal
        });
      } else {
        setIPLocation({ error: response.data.reason || 'Could not locate IP' });
      }
    } catch (error) {
      console.error('IP lookup error:', error);
      setIPLocation({ error: 'Failed to fetch location data. Rate limit may have been exceeded.' });
    } finally {
      setIPLoading(false);
    }
  };

  const openGoogleMaps = () => {
    if (ipLocation && ipLocation.lat && ipLocation.lon) {
      window.open(`https://www.google.com/maps?q=${ipLocation.lat},${ipLocation.lon}`, '_blank');
    }
  };

  // User Profile handling
  const handleUserClick = async (user) => {
    setSelectedUser(user);
    setShowUserProfile(true);
    setUserProfileLoading(true);
    setUserProfileData(null);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/user/${user.id}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserProfileData(response.data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      toast.error('Failed to load user profile');
    } finally {
      setUserProfileLoading(false);
    }
  };

  // Delete user handler
  const handleDeleteUser = async () => {
    if (!selectedUser?.id) return;
    
    setDeletingUser(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(`${API}/admin/user/${selectedUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`User "${selectedUser.username}" deleted successfully`);
      setShowDeleteUserConfirm(false);
      setShowUserProfile(false);
      setSelectedUser(null);
      
      // Refresh users list
      fetchAllData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUser(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ============ RENDER FUNCTIONS ============

  const renderStats = () => (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
      {[
        { label: 'Users', value: stats?.users || 0, icon: Users, color: '#7C3AED' },
        { label: 'Chats', value: stats?.conversations || 0, icon: MessageSquare, color: '#22D3EE' },
        { label: 'Messages', value: stats?.messages || 0, icon: Send, color: '#22C55E' },
        { label: 'Calls', value: stats?.calls || 0, icon: Phone, color: '#F59E0B' },
        { label: 'Invites', value: stats?.invites || 0, icon: TrendingUp, color: '#EC4899' },
      ].map((stat) => (
        <Card key={stat.label} className="border-0" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ background: `${stat.color}20` }}>
              <stat.icon className="h-5 w-5" style={{ color: stat.color }} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: '#E5E7EB' }}>{stat.value}</p>
              <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>{stat.label}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  // Quick delete user from list
  const handleQuickDeleteUser = async (e, user) => {
    e.stopPropagation(); // Prevent row click
    
    if (!window.confirm(`Delete user "${user.username || user.email || user.phone}"?\n\nThis will permanently delete all their data including messages, conversations, and invites.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/user/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`User "${user.username || 'Unknown'}" deleted`);
      
      // Refresh users list
      fetchAllData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Open SMS dialog for a user
  const openSMSDialog = (e, user) => {
    e.stopPropagation();
    if (!user.phone) {
      toast.error('This user has no phone number');
      return;
    }
    setSmsTargetUser(user);
    setSmsMessage(DEFAULT_SMS_TEMPLATE);
    setShowSMSDialog(true);
  };

  // Send SMS to user
  const sendSMSToUser = async () => {
    if (!smsTargetUser || !smsMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSendingSMS(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/admin/send-bulk-sms`, {
        message: smsMessage,
        user_ids: [smsTargetUser.id],
        filter_agreed_only: false,
        dry_run: false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.sent_count > 0) {
        toast.success(`SMS sent to ${smsTargetUser.username || smsTargetUser.phone}`);
        setShowSMSDialog(false);
        setShowSMSConfirm(false);
        setSmsTargetUser(null);
        setSmsMessage('');
      } else {
        toast.error('Failed to send SMS');
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSendingSMS(false);
    }
  };

  const renderUsersList = () => (
    <Card className="border-0" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
      <CardHeader>
        <CardTitle style={{ color: '#E5E7EB' }}>All Users</CardTitle>
        <CardDescription style={{ color: 'rgba(229,231,235,0.5)' }}>{users.length} registered users</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th className="text-left py-3 px-2" style={{ color: 'rgba(229,231,235,0.7)' }}>User</th>
                <th className="text-left py-3 px-2" style={{ color: 'rgba(229,231,235,0.7)' }}>Phone</th>
                <th className="text-left py-3 px-2" style={{ color: 'rgba(229,231,235,0.7)' }}>Email</th>
                <th className="text-left py-3 px-2" style={{ color: 'rgba(229,231,235,0.7)' }}>IP Address</th>
                <th className="text-left py-3 px-2" style={{ color: 'rgba(229,231,235,0.7)' }}>Joined</th>
                <th className="text-center py-3 px-2" style={{ color: 'rgba(229,231,235,0.7)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr 
                  key={user.id} 
                  onClick={() => handleUserClick(user)}
                  className="hover:bg-white/5 cursor-pointer transition-colors"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                >
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-2">
                      {user.profile_picture ? (
                        <img 
                          src={user.profile_picture.startsWith('/') ? `${API.replace('/api', '')}${user.profile_picture}` : user.profile_picture} 
                          alt={user.username} 
                          className="w-8 h-8 rounded-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                          }}
                        />
                      ) : null}
                      <div className={`w-8 h-8 rounded-full items-center justify-center ${user.profile_picture ? 'hidden' : 'flex'}`} style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}>
                        <User className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <span style={{ color: '#E5E7EB' }}>{user.username || 'No username'}</span>
                        {user.login_count > 0 && (
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(124, 58, 237, 0.2)', color: '#A78BFA' }}>
                            {user.login_count} logins
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-2" style={{ color: 'rgba(229,231,235,0.7)' }}>{user.phone || '-'}</td>
                  <td className="py-3 px-2" style={{ color: 'rgba(229,231,235,0.7)' }}>{user.email || '-'}</td>
                  <td className="py-3 px-2">
                    {user.ip_address ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleIPClick(user.ip_address); }}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-md transition-all hover:bg-white/10"
                        style={{ color: '#22D3EE' }}
                      >
                        <MapPin className="h-3.5 w-3.5" />
                        <span className="text-sm">{user.ip_address?.split(',')[0]?.slice(0, 15)}{user.ip_address?.length > 15 ? '...' : ''}</span>
                      </button>
                    ) : (
                      <span style={{ color: 'rgba(229,231,235,0.5)' }}>-</span>
                    )}
                  </td>
                  <td className="py-3 px-2" style={{ color: 'rgba(229,231,235,0.5)' }}>{formatDate(user.created_at)}</td>
                  <td className="py-3 px-2">
                    <div className="flex items-center justify-center gap-2">
                      {/* SMS Button */}
                      <button
                        onClick={(e) => openSMSDialog(e, user)}
                        className={`p-1.5 rounded-md transition-all ${user.phone ? 'hover:bg-purple-500/20' : 'opacity-30 cursor-not-allowed'}`}
                        style={{ color: user.phone ? '#A78BFA' : 'rgba(229,231,235,0.3)' }}
                        title={user.phone ? 'Send SMS' : 'No phone number'}
                        disabled={!user.phone}
                      >
                        <Send className="h-4 w-4" />
                      </button>
                      {/* Delete Button */}
                      <button
                        onClick={(e) => handleQuickDeleteUser(e, user)}
                        className="p-1.5 rounded-md transition-all hover:bg-red-500/20"
                        style={{ color: '#EF4444' }}
                        title="Delete user"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <ChevronRight className="h-4 w-4" style={{ color: 'rgba(229,231,235,0.3)' }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );

  const renderChatsList = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ minHeight: 'calc(100vh - 320px)' }}>
      {/* Chat List */}
      <Card className="border-0 lg:col-span-1 flex flex-col" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', maxHeight: 'calc(100vh - 320px)' }}>
        <CardHeader className="pb-2 shrink-0">
          <CardTitle style={{ color: '#E5E7EB' }}>All Chats</CardTitle>
          <CardDescription style={{ color: 'rgba(229,231,235,0.5)' }}>
            {chatsLoading ? 'Loading...' : `${conversations.length} conversations`}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0 flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto" style={{ scrollBehavior: 'smooth' }}>
            {conversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => fetchChatDetail(conv.id)}
                className={`w-full p-4 text-left transition-colors hover:bg-white/5 ${selectedChat === conv.id ? 'bg-white/10' : ''}`}
                style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <MessageSquare className="h-4 w-4 shrink-0" style={{ color: '#7C3AED' }} />
                      <span className="font-medium truncate text-sm" style={{ color: '#E5E7EB' }}>
                        {conv.participants?.[0]?.username || 'User 1'} 
                        {conv.participants?.[0]?.type === 'guest' && (
                          <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0" style={{ borderColor: '#F59E0B', color: '#F59E0B' }}>Guest</Badge>
                        )}
                        &nbsp;&&nbsp;
                        {conv.participants?.[1]?.username || 'User 2'}
                        {conv.participants?.[1]?.is_guest && (
                          <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0" style={{ borderColor: '#F59E0B', color: '#F59E0B' }}>
                            {conv.participants?.[1]?.converted ? 'Converted' : 'Guest'}
                          </Badge>
                        )}
                      </span>
                      {conv.is_invite_chat && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0" style={{ borderColor: '#22D3EE', color: '#22D3EE' }}>Invite</Badge>
                      )}
                    </div>
                    <p className="text-xs truncate" style={{ color: 'rgba(229,231,235,0.5)' }}>
                      {conv.last_message || 'No messages yet'}
                    </p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs flex items-center gap-1" style={{ color: 'rgba(229,231,235,0.4)' }}>
                        <Send className="h-3 w-3" /> {conv.message_count}
                      </span>
                      <span className="text-xs flex items-center gap-1" style={{ color: 'rgba(229,231,235,0.4)' }}>
                        <Phone className="h-3 w-3" /> {conv.call_count}
                      </span>
                      {conv.disappearing_enabled && (
                        <span className="text-xs flex items-center gap-1" style={{ color: '#F59E0B' }}>
                          <Timer className="h-3 w-3" /> {conv.disappearing_timer}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs" style={{ color: 'rgba(229,231,235,0.4)' }}>
                      {formatDate(conv.last_activity)}
                    </p>
                    <ChevronRight className="h-4 w-4 mt-2 ml-auto" style={{ color: 'rgba(229,231,235,0.3)' }} />
                  </div>
                </div>
              </button>
            ))}
            {conversations.length === 0 && (
              <div className="p-8 text-center" style={{ color: 'rgba(229,231,235,0.5)' }}>
                No conversations yet
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Chat Detail View */}
      <Card className="border-0 lg:col-span-2 flex flex-col" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', maxHeight: 'calc(100vh - 320px)' }}>
        {!selectedChat ? (
          <div className="h-full min-h-[400px] flex items-center justify-center" style={{ color: 'rgba(229,231,235,0.5)' }}>
            <div className="text-center">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>Select a chat to view conversation</p>
            </div>
          </div>
        ) : chatLoading ? (
          <div className="h-full min-h-[400px] flex items-center justify-center" style={{ color: 'rgba(229,231,235,0.5)' }}>
            Loading...
          </div>
        ) : chatData ? (
          <div className="flex flex-col h-full overflow-hidden">
            {/* Chat Header */}
            <div className="p-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold" style={{ color: '#E5E7EB' }}>
                    Chat ID: {chatData.conversation?.id?.slice(0, 8)}...
                  </h3>
                  <p className="text-xs mt-1" style={{ color: 'rgba(229,231,235,0.5)' }}>
                    {chatData.stats?.total_messages} messages • {chatData.stats?.total_calls} calls
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {chatData.stats?.disappearing_enabled && (
                    <Badge style={{ background: 'rgba(245,158,11,0.2)', color: '#F59E0B', border: 'none' }}>
                      <Timer className="h-3 w-3 mr-1" />
                      {chatData.stats?.disappearing_timer}
                    </Badge>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('adminToken');
                        const response = await axios.get(
                          `${API}/admin/conversations/${selectedChat}/export?include_media=true`,
                          { headers: { Authorization: `Bearer ${token}` }}
                        );
                        
                        // Download as JSON file
                        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `conversation_${selectedChat.slice(0, 8)}_export.json`;
                        a.click();
                        URL.revokeObjectURL(url);
                        toast.success('Conversation exported!');
                      } catch (error) {
                        toast.error('Failed to export conversation');
                      }
                    }}
                  >
                    <Download className="h-4 w-4 mr-1" /> Export
                  </Button>
                </div>
              </div>
              
              {/* Participants */}
              <div className="flex gap-4 mt-3">
                {chatData.participants?.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs" style={{ color: 'rgba(229,231,235,0.7)' }}>
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold" 
                         style={{ background: i === 0 ? '#7C3AED' : '#22D3EE', color: 'white' }}>
                      {(p.username || 'U')[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium">{p.username || 'Unknown'}</p>
                      <p className="text-[10px]" style={{ color: 'rgba(229,231,235,0.4)' }}>
                        {p.phone || p.email || 'No contact'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Messages */}
            <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3" style={{ scrollBehavior: 'smooth' }}>
              {chatData.timeline?.map((item, idx) => {
                if (item.type === 'system') {
                  return (
                    <div key={idx} className="flex justify-center">
                      <div className="px-3 py-1.5 rounded-full text-xs flex items-center gap-2"
                           style={{ background: 'rgba(124,58,237,0.15)', color: 'rgba(229,231,235,0.7)' }}>
                        <Settings className="h-3 w-3" />
                        {item.data.message}
                      </div>
                    </div>
                  );
                }

                if (item.type === 'call') {
                  const call = item.data;
                  const isVoice = call.call_type === 'audio';
                  const isMissed = call.status === 'missed';
                  // Check if recording URL looks valid (not a test/mock URL)
                  const hasCallerRecording = call.caller_recording_url || (call.recording_url && 
                    !call.recording_url.includes('test_audio_') && 
                    !call.recording_url.includes('test_video_'));
                  const hasReceiverRecording = call.receiver_recording_url;
                  const hasAnyRecording = hasCallerRecording || hasReceiverRecording;
                  
                  return (
                    <div key={idx} className="flex justify-center">
                      <div className="p-3 rounded-xl text-sm"
                           style={{ 
                             background: isMissed ? 'rgba(239,68,68,0.1)' : 'rgba(34,211,238,0.1)', 
                             border: isMissed ? '1px solid rgba(239,68,68,0.2)' : '1px solid rgba(34,211,238,0.2)' 
                           }}>
                        <div className="flex items-center gap-3">
                          {isVoice ? (
                            <PhoneCall className="h-5 w-5" style={{ color: isMissed ? '#EF4444' : '#22D3EE' }} />
                          ) : (
                            <Video className="h-5 w-5" style={{ color: isMissed ? '#EF4444' : '#22D3EE' }} />
                          )}
                          <div>
                            <p className="font-medium" style={{ color: '#E5E7EB' }}>
                              {isMissed ? 'Missed' : ''} {isVoice ? 'Voice Call' : 'Video Call'} 
                              {!isMissed && ` — ${formatDuration(call.duration)}`}
                            </p>
                            <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>
                              {call.caller_name} → {call.receiver_name}
                            </p>
                            <p className="text-xs mt-1" style={{ color: 'rgba(229,231,235,0.4)' }}>
                              {formatDate(call.started_at || call.created_at)}
                            </p>
                            {/* Call ID with copy button */}
                            <div className="flex items-center gap-1 mt-1">
                              <code className="text-xs px-1 py-0.5 rounded" style={{ color: 'rgba(229,231,235,0.3)', background: 'rgba(0,0,0,0.3)' }}>
                                {call.id?.slice(0, 8)}...
                              </code>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(call.id);
                                  toast.success('Call ID copied!');
                                }}
                                className="p-0.5 rounded hover:bg-white/10 transition-colors"
                                title="Copy full Call ID"
                              >
                                <Copy className="h-3 w-3" style={{ color: 'rgba(229,231,235,0.4)' }} />
                              </button>
                            </div>
                          </div>
                          {hasAnyRecording ? (
                            <div className="ml-auto flex gap-2">
                              {hasCallerRecording && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-cyan-400 hover:text-cyan-300"
                                  onClick={() => {
                                    const recordingUrl = call.caller_recording_url || call.recording_url;
                                    setSelectedRecording({
                                      url: recordingUrl.startsWith('/') ? `${API.replace('/api', '')}${recordingUrl}` : recordingUrl,
                                      type: call.call_type,
                                      caller: call.caller_name,
                                      receiver: call.receiver_name,
                                      duration: call.duration,
                                      date: call.started_at || call.created_at,
                                      userRole: 'Caller'
                                    });
                                    setRecordingError(false);
                                    setMediaLoaded(false);
                                    setShowRecordingModal(true);
                                  }}
                                >
                                  <Play className="h-4 w-4 mr-1" /> {call.caller_name?.split(' ')[0] || 'Caller'}
                                </Button>
                              )}
                              {hasReceiverRecording && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-purple-400 hover:text-purple-300"
                                  onClick={() => {
                                    setSelectedRecording({
                                      url: call.receiver_recording_url.startsWith('/') ? `${API.replace('/api', '')}${call.receiver_recording_url}` : call.receiver_recording_url,
                                      type: call.call_type,
                                      caller: call.caller_name,
                                      receiver: call.receiver_name,
                                      duration: call.duration,
                                      date: call.started_at || call.created_at,
                                      userRole: 'Receiver'
                                    });
                                    setRecordingError(false);
                                    setMediaLoaded(false);
                                    setShowRecordingModal(true);
                                  }}
                                >
                                  <Play className="h-4 w-4 mr-1" /> {call.receiver_name?.split(' ')[0] || 'Receiver'}
                                </Button>
                              )}
                            </div>
                          ) : (
                            <span className="ml-auto text-xs px-2 py-1 rounded" style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(229,231,235,0.4)' }}>
                              No recording
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                // Cancelled recording - show with red border
                if (item.type === 'cancelled_recording') {
                  const rec = item.data;
                  const isUser1 = chatData.participants?.[0]?.user_id === rec.user_id;
                  const audioUrl = rec.file_url?.startsWith('/') 
                    ? `${API.replace('/api', '')}${rec.file_url}` 
                    : rec.file_url;
                  
                  return (
                    <div key={idx} className={`flex ${isUser1 ? 'justify-start' : 'justify-end'}`}>
                      <div className="max-w-[70%]">
                        <div className="text-xs mb-1 flex items-center gap-1" style={{ color: '#EF4444' }}>
                          <X className="h-3 w-3" />
                          <span>Cancelled Recording (not sent to user)</span>
                        </div>
                        <div 
                          className="px-3 py-2 rounded-2xl"
                          style={{ 
                            background: 'rgba(239,68,68,0.1)', 
                            border: '1px dashed rgba(239,68,68,0.5)',
                            opacity: 0.7
                          }}
                        >
                          <p className="text-xs font-medium mb-1" style={{ color: '#EF4444' }}>
                            {rec.sender_username || 'Unknown'}
                          </p>
                          <div className="flex items-center gap-2">
                            <audio 
                              src={audioUrl}
                              controls
                              preload="metadata"
                              className="flex-1 max-w-[200px]"
                              style={{ height: '40px' }}
                            />
                            <a 
                              href={audioUrl}
                              download
                              className="p-2 rounded-lg hover:bg-white/10 transition"
                              title="Download"
                            >
                              <Download className="h-4 w-4" style={{ color: '#EF4444' }} />
                            </a>
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <p className="text-[10px]" style={{ color: '#EF4444' }}>
                              Duration: {rec.duration || 0}s
                            </p>
                            <p className="text-[10px]" style={{ color: 'rgba(229,231,235,0.4)' }}>
                              {rec.created_at ? new Date(rec.created_at).toLocaleTimeString() : ''}
                            </p>
                          </div>
                          {/* Copyable Recording ID */}
                          <div className="flex items-center gap-1 mt-1">
                            <code className="text-[9px] px-1 py-0.5 rounded" style={{ color: 'rgba(239,68,68,0.6)', background: 'rgba(0,0,0,0.3)' }}>
                              ID: {rec.id?.slice(0, 8)}...
                            </code>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(rec.id);
                                toast.success('Recording ID copied!');
                              }}
                              className="p-0.5 rounded hover:bg-white/10 transition-colors"
                              title="Copy full Recording ID"
                            >
                              <Copy className="h-3 w-3" style={{ color: 'rgba(239,68,68,0.5)' }} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Regular message
                const msg = item.data;
                const isUser1 = chatData.participants?.[0]?.user_id === msg.from_user_id;
                const isMediaMessage = msg.message_type === 'image' || msg.message_type === 'video';
                const isAudioMessage = msg.message_type === 'audio';
                const isExpired = msg.is_expired;
                
                return (
                  <div key={idx} className={`flex ${isUser1 ? 'justify-start' : 'justify-end'}`}>
                    <div className="max-w-[70%]">
                      {/* Expired indicator */}
                      {isExpired && (
                        <div className="text-xs mb-1 flex items-center gap-1" style={{ color: '#EF4444' }}>
                          <Clock className="h-3 w-3" />
                          <span>Hidden from user (expired)</span>
                        </div>
                      )}
                      <div className={`px-3 py-2 rounded-2xl ${isUser1 ? 'rounded-bl-sm' : 'rounded-br-sm'} ${isExpired ? 'opacity-60' : ''}`}
                           style={{ 
                             background: isExpired 
                               ? 'rgba(239,68,68,0.1)' 
                               : (isUser1 ? 'rgba(124,58,237,0.2)' : 'rgba(34,211,238,0.2)'),
                             border: `1px solid ${isExpired ? 'rgba(239,68,68,0.3)' : (isUser1 ? 'rgba(124,58,237,0.3)' : 'rgba(34,211,238,0.3)')}`
                           }}>
                        <p className="text-xs font-medium mb-1" style={{ color: isUser1 ? '#A78BFA' : '#22D3EE' }}>
                          {msg.sender_username || 'Unknown'}
                        </p>
                        
                        {/* Audio message - ALWAYS show for admin, even if view-once opened */}
                        {isAudioMessage && msg.media_urls?.length > 0 && (() => {
                          const audioUrl = getSecureMediaUrl(msg.media_urls[0]);
                          console.log('Audio URL:', audioUrl);
                          return (
                          <div className="mb-2">
                            {msg.view_once && msg.view_once_opened && (
                              <p className="text-[10px] mb-1" style={{ color: '#F59E0B' }}>
                                ⚠️ View-once audio (opened by user, but admin can still play)
                              </p>
                            )}
                            <div className="flex items-center gap-2">
                              <audio 
                                src={audioUrl}
                                controls
                                preload="metadata"
                                className="flex-1 max-w-[200px]"
                                style={{ height: '40px' }}
                                onError={(e) => console.error('Audio error:', e.target.error)}
                              />
                              <a 
                                href={audioUrl}
                                download
                                className="p-2 rounded-lg hover:bg-white/10 transition"
                                title="Download audio"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cyan-400">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                  <polyline points="7 10 12 15 17 10"/>
                                  <line x1="12" y1="15" x2="12" y2="3"/>
                                </svg>
                              </a>
                            </div>
                            <p className="text-[9px] mt-1" style={{ color: 'rgba(229,231,235,0.4)' }}>
                              {msg.media_urls[0].split('/').pop()}
                            </p>
                            {/* Copyable Message ID for audio */}
                            <div className="flex items-center gap-1 mt-1">
                              <code className="text-[9px] px-1 py-0.5 rounded" style={{ color: 'rgba(34,211,238,0.6)', background: 'rgba(0,0,0,0.3)' }}>
                                ID: {msg.id?.slice(0, 8)}...
                              </code>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(msg.id);
                                  toast.success('Audio Message ID copied!');
                                }}
                                className="p-0.5 rounded hover:bg-white/10 transition-colors"
                                title="Copy full Message ID"
                              >
                                <Copy className="h-3 w-3" style={{ color: 'rgba(34,211,238,0.5)' }} />
                              </button>
                            </div>
                          </div>
                          );
                        })()}
                        
                        {/* Media content */}
                        {isMediaMessage && msg.media_urls?.length > 0 && (
                          <div className="mb-2">
                            {msg.message_type === 'image' ? (
                              <img 
                                src={getSecureMediaUrl(msg.media_urls[0])} 
                                alt="Shared image"
                                className="max-w-full h-auto rounded-lg cursor-pointer hover:opacity-90"
                                style={{ maxHeight: '200px' }}
                                onClick={() => window.open(getSecureMediaUrl(msg.media_urls[0]), '_blank')}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                                }}
                              />
                            ) : (
                              <video 
                                src={getSecureMediaUrl(msg.media_urls[0])}
                                controls
                                className="max-w-full h-auto rounded-lg"
                                style={{ maxHeight: '200px' }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                            <div className="hidden items-center justify-center p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
                              <span className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>Media unavailable</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Text message */}
                        {msg.message && (
                          <p className="text-sm" style={{ color: '#E5E7EB' }}>{msg.message}</p>
                        )}
                        
                        <div className="flex items-center justify-between gap-2 mt-1">
                          <div className="flex items-center gap-2">
                            <p className="text-[10px]" style={{ color: 'rgba(229,231,235,0.4)' }}>
                              {formatTime(msg.created_at)}
                            </p>
                            {isMediaMessage && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(245,158,11,0.2)', color: '#F59E0B' }}>
                                {msg.message_type === 'image' ? '📷 Image' : '🎬 Video'}
                              </span>
                            )}
                            {isAudioMessage && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(34,211,238,0.2)', color: '#22D3EE' }}>
                                🎤 Audio
                              </span>
                            )}
                            {msg.view_once && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: msg.view_once_opened ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)', color: msg.view_once_opened ? '#EF4444' : '#F59E0B' }}>
                                🔥 {msg.view_once_opened ? 'Viewed' : 'View Once'}
                              </span>
                            )}
                          </div>
                          {msg.is_expired && (
                            <span className="text-[10px] flex items-center gap-1" style={{ color: '#F59E0B' }}>
                              <AlertTriangle className="h-2.5 w-2.5" /> Expired for users
                            </span>
                          )}
                        </div>
                      </div>
                      {msg.expires_at && (
                        <p className="text-[10px] mt-0.5 px-2" style={{ color: 'rgba(229,231,235,0.3)' }}>
                          Expires: {formatDate(msg.expires_at)}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );

  const renderInvites = () => (
    <div className="space-y-4">
      {/* Header with Download Button */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold" style={{ color: '#E5E7EB' }}>Invite Analytics</h2>
          <p className="text-sm" style={{ color: 'rgba(229,231,235,0.5)' }}>Track and export all invite data</p>
        </div>
        <Button
          onClick={downloadInvitesExcel}
          disabled={downloadingExcel}
          className="flex items-center gap-2"
          style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
          data-testid="download-invites-btn"
        >
          {downloadingExcel ? (
            <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          {downloadingExcel ? 'Downloading...' : 'Download Excel'}
        </Button>
      </div>
      
      {/* Analytics Cards */}
      {inviteAnalytics && (
        <div className="space-y-4">
          {/* Conversion Funnel Visualization */}
          <Card className="border-0" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#E5E7EB' }}>
                <TrendingUp className="h-5 w-5" style={{ color: '#7C3AED' }} />
                Conversion Funnel
              </CardTitle>
              <CardDescription style={{ color: 'rgba(229,231,235,0.5)' }}>
                Track invite performance from send to user conversion
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-2 py-4">
                {/* Sent Stage */}
                <div className="flex-1 text-center">
                  <div className="relative mx-auto w-24 h-24 rounded-full flex items-center justify-center" 
                       style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.3) 0%, rgba(124,58,237,0.1) 100%)', border: '2px solid rgba(124,58,237,0.5)' }}>
                    <div className="text-center">
                      <p className="text-2xl font-bold" style={{ color: '#A78BFA' }}>{inviteAnalytics.funnel?.sent || inviteAnalytics.total_invites || 0}</p>
                      <p className="text-xs" style={{ color: 'rgba(229,231,235,0.6)' }}>Sent</p>
                    </div>
                  </div>
                </div>
                
                {/* Arrow & Rate */}
                <div className="flex flex-col items-center">
                  <ChevronRight className="h-6 w-6" style={{ color: 'rgba(229,231,235,0.3)' }} />
                  <span className="text-xs font-medium mt-1" style={{ color: '#22D3EE' }}>{inviteAnalytics.open_rate || 0}%</span>
                </div>
                
                {/* Opened Stage */}
                <div className="flex-1 text-center">
                  <div className="relative mx-auto w-24 h-24 rounded-full flex items-center justify-center" 
                       style={{ background: 'linear-gradient(135deg, rgba(34,211,238,0.3) 0%, rgba(34,211,238,0.1) 100%)', border: '2px solid rgba(34,211,238,0.5)' }}>
                    <div className="text-center">
                      <p className="text-2xl font-bold" style={{ color: '#22D3EE' }}>{inviteAnalytics.funnel?.opened || inviteAnalytics.total_opens || 0}</p>
                      <p className="text-xs" style={{ color: 'rgba(229,231,235,0.6)' }}>Opened</p>
                    </div>
                  </div>
                </div>
                
                {/* Arrow & Rate */}
                <div className="flex flex-col items-center">
                  <ChevronRight className="h-6 w-6" style={{ color: 'rgba(229,231,235,0.3)' }} />
                  <span className="text-xs font-medium mt-1" style={{ color: '#F59E0B' }}>{inviteAnalytics.join_rate || 0}%</span>
                </div>
                
                {/* Joined Stage */}
                <div className="flex-1 text-center">
                  <div className="relative mx-auto w-24 h-24 rounded-full flex items-center justify-center" 
                       style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.3) 0%, rgba(245,158,11,0.1) 100%)', border: '2px solid rgba(245,158,11,0.5)' }}>
                    <div className="text-center">
                      <p className="text-2xl font-bold" style={{ color: '#F59E0B' }}>{inviteAnalytics.funnel?.joined || inviteAnalytics.joined || 0}</p>
                      <p className="text-xs" style={{ color: 'rgba(229,231,235,0.6)' }}>Joined</p>
                    </div>
                  </div>
                </div>
                
                {/* Arrow & Rate */}
                <div className="flex flex-col items-center">
                  <ChevronRight className="h-6 w-6" style={{ color: 'rgba(229,231,235,0.3)' }} />
                  <span className="text-xs font-medium mt-1" style={{ color: '#22C55E' }}>{inviteAnalytics.conversion_rate || 0}%</span>
                </div>
                
                {/* Converted Stage */}
                <div className="flex-1 text-center">
                  <div className="relative mx-auto w-24 h-24 rounded-full flex items-center justify-center" 
                       style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.3) 0%, rgba(34,197,94,0.1) 100%)', border: '2px solid rgba(34,197,94,0.5)' }}>
                    <div className="text-center">
                      <p className="text-2xl font-bold" style={{ color: '#22C55E' }}>{inviteAnalytics.funnel?.converted || inviteAnalytics.converted || 0}</p>
                      <p className="text-xs" style={{ color: 'rgba(229,231,235,0.6)' }}>Users</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Per-Channel Conversion Rates */}
          {inviteAnalytics.by_channel && Object.keys(inviteAnalytics.by_channel).length > 0 && (
            <Card className="border-0" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#E5E7EB' }}>
                  <Activity className="h-5 w-5" style={{ color: '#22D3EE' }} />
                  Channel Performance
                </CardTitle>
                <CardDescription style={{ color: 'rgba(229,231,235,0.5)' }}>
                  Compare conversion rates across invite channels
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Email Channel */}
                  {inviteAnalytics.by_channel.email && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4" style={{ color: '#22D3EE' }} />
                          <span className="text-sm font-medium" style={{ color: '#E5E7EB' }}>Email</span>
                          <Badge variant="outline" className="text-xs" style={{ borderColor: 'rgba(34,211,238,0.3)', color: '#22D3EE' }}>
                            {inviteAnalytics.by_channel.email.total} invites
                          </Badge>
                        </div>
                        <span className="text-sm font-bold" style={{ color: '#22C55E' }}>
                          {inviteAnalytics.by_channel.email.conversion_rate}% converted
                        </span>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                        <div className="h-full flex">
                          <div style={{ width: `${inviteAnalytics.by_channel.email.open_rate}%`, background: '#22D3EE' }} />
                          <div style={{ width: `${Math.max(0, inviteAnalytics.by_channel.email.join_rate - inviteAnalytics.by_channel.email.conversion_rate)}%`, background: '#F59E0B' }} />
                          <div style={{ width: `${inviteAnalytics.by_channel.email.conversion_rate}%`, background: '#22C55E' }} />
                        </div>
                      </div>
                      <div className="flex justify-between text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>
                        <span>{inviteAnalytics.by_channel.email.open_rate}% opened</span>
                        <span>{inviteAnalytics.by_channel.email.join_rate}% joined</span>
                        <span>{inviteAnalytics.by_channel.email.conversion_rate}% converted</span>
                      </div>
                    </div>
                  )}
                  
                  {/* SMS Channel */}
                  {inviteAnalytics.by_channel.sms && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4" style={{ color: '#22C55E' }} />
                          <span className="text-sm font-medium" style={{ color: '#E5E7EB' }}>SMS</span>
                          <Badge variant="outline" className="text-xs" style={{ borderColor: 'rgba(34,197,94,0.3)', color: '#22C55E' }}>
                            {inviteAnalytics.by_channel.sms.total} invites
                          </Badge>
                        </div>
                        <span className="text-sm font-bold" style={{ color: '#22C55E' }}>
                          {inviteAnalytics.by_channel.sms.conversion_rate}% converted
                        </span>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                        <div className="h-full flex">
                          <div style={{ width: `${inviteAnalytics.by_channel.sms.open_rate}%`, background: '#22D3EE' }} />
                          <div style={{ width: `${Math.max(0, inviteAnalytics.by_channel.sms.join_rate - inviteAnalytics.by_channel.sms.conversion_rate)}%`, background: '#F59E0B' }} />
                          <div style={{ width: `${inviteAnalytics.by_channel.sms.conversion_rate}%`, background: '#22C55E' }} />
                        </div>
                      </div>
                      <div className="flex justify-between text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>
                        <span>{inviteAnalytics.by_channel.sms.open_rate}% opened</span>
                        <span>{inviteAnalytics.by_channel.sms.join_rate}% joined</span>
                        <span>{inviteAnalytics.by_channel.sms.conversion_rate}% converted</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Copy Link Channel */}
                  {inviteAnalytics.by_channel.copy && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Copy className="h-4 w-4" style={{ color: '#A78BFA' }} />
                          <span className="text-sm font-medium" style={{ color: '#E5E7EB' }}>Copy Link</span>
                          <Badge variant="outline" className="text-xs" style={{ borderColor: 'rgba(167,139,250,0.3)', color: '#A78BFA' }}>
                            {inviteAnalytics.by_channel.copy.total} invites
                          </Badge>
                        </div>
                        <span className="text-sm font-bold" style={{ color: '#22C55E' }}>
                          {inviteAnalytics.by_channel.copy.conversion_rate}% converted
                        </span>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                        <div className="h-full flex">
                          <div style={{ width: `${inviteAnalytics.by_channel.copy.open_rate}%`, background: '#22D3EE' }} />
                          <div style={{ width: `${Math.max(0, inviteAnalytics.by_channel.copy.join_rate - inviteAnalytics.by_channel.copy.conversion_rate)}%`, background: '#F59E0B' }} />
                          <div style={{ width: `${inviteAnalytics.by_channel.copy.conversion_rate}%`, background: '#22C55E' }} />
                        </div>
                      </div>
                      <div className="flex justify-between text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>
                        <span>{inviteAnalytics.by_channel.copy.open_rate}% opened</span>
                        <span>{inviteAnalytics.by_channel.copy.join_rate}% joined</span>
                        <span>{inviteAnalytics.by_channel.copy.conversion_rate}% converted</span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Recent Activity & Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border-0" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <CardContent className="p-4">
                <p className="text-2xl font-bold" style={{ color: '#E5E7EB' }}>{inviteAnalytics.total_invites || 0}</p>
                <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>Total Invites</p>
              </CardContent>
            </Card>
            <Card className="border-0" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <CardContent className="p-4">
                <p className="text-2xl font-bold" style={{ color: '#F59E0B' }}>{inviteAnalytics.pending || 0}</p>
                <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>Pending</p>
              </CardContent>
            </Card>
            <Card className="border-0" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <CardContent className="p-4">
                <p className="text-2xl font-bold" style={{ color: '#22D3EE' }}>{inviteAnalytics.recent?.invites_7d || 0}</p>
                <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>Last 7 Days</p>
              </CardContent>
            </Card>
            <Card className="border-0" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <CardContent className="p-4">
                <p className="text-2xl font-bold" style={{ color: '#22C55E' }}>{inviteAnalytics.recent?.conversion_7d || 0}%</p>
                <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>7-Day Conv. Rate</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Method Breakdown (Legacy Cards) */}
          {inviteAnalytics.by_method && (
            <div className="grid grid-cols-3 gap-3">
              <Card className="border-0" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)' }}>
                <CardContent className="p-3 flex items-center gap-3">
                  <Copy className="h-5 w-5" style={{ color: '#A78BFA' }} />
                  <div>
                    <p className="text-lg font-bold" style={{ color: '#E5E7EB' }}>{inviteAnalytics.by_method.link || 0}</p>
                    <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>Copy Link</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0" style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)' }}>
                <CardContent className="p-3 flex items-center gap-3">
                  <Mail className="h-5 w-5" style={{ color: '#22D3EE' }} />
                  <div>
                    <p className="text-lg font-bold" style={{ color: '#E5E7EB' }}>{inviteAnalytics.by_method.email || 0}</p>
                    <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>Via Email</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
                <CardContent className="p-3 flex items-center gap-3">
                  <Phone className="h-5 w-5" style={{ color: '#22C55E' }} />
                  <div>
                    <p className="text-lg font-bold" style={{ color: '#E5E7EB' }}>{inviteAnalytics.by_method.phone || 0}</p>
                    <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>Via SMS</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* OTP Verification Funnel for Link Invites */}
          {inviteAnalytics.otp_funnel && inviteAnalytics.otp_funnel.total_link_invites > 0 && (
            <Card className="border-0" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium flex items-center gap-2" style={{ color: '#E5E7EB' }}>
                  <Shield className="h-4 w-4" style={{ color: '#7C3AED' }} />
                  Link Invite OTP Funnel
                </CardTitle>
                <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>
                  Track where users drop off during link invite verification
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* OTP Funnel Visualization */}
                <div className="flex items-center justify-between gap-2">
                  {/* Created */}
                  <div className="flex-1 text-center">
                    <div className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center" style={{ background: 'rgba(167,139,250,0.2)', border: '2px solid #A78BFA' }}>
                      <span className="text-sm font-bold" style={{ color: '#A78BFA' }}>{inviteAnalytics.otp_funnel.total_link_invites}</span>
                    </div>
                    <p className="text-xs" style={{ color: 'rgba(229,231,235,0.6)' }}>Created</p>
                  </div>
                  <div style={{ color: 'rgba(229,231,235,0.3)' }}>→</div>
                  
                  {/* Opened */}
                  <div className="flex-1 text-center">
                    <div className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.2)', border: '2px solid #22D3EE' }}>
                      <span className="text-sm font-bold" style={{ color: '#22D3EE' }}>{inviteAnalytics.otp_funnel.link_opened}</span>
                    </div>
                    <p className="text-xs" style={{ color: 'rgba(229,231,235,0.6)' }}>Opened</p>
                  </div>
                  <div style={{ color: 'rgba(229,231,235,0.3)' }}>→</div>
                  
                  {/* OTP Sent */}
                  <div className="flex-1 text-center">
                    <div className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.2)', border: '2px solid #F59E0B' }}>
                      <span className="text-sm font-bold" style={{ color: '#F59E0B' }}>{inviteAnalytics.otp_funnel.otp_requested}</span>
                    </div>
                    <p className="text-xs" style={{ color: 'rgba(229,231,235,0.6)' }}>OTP Sent</p>
                  </div>
                  <div style={{ color: 'rgba(229,231,235,0.3)' }}>→</div>
                  
                  {/* Verified */}
                  <div className="flex-1 text-center">
                    <div className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center" style={{ background: 'rgba(59,130,246,0.2)', border: '2px solid #3B82F6' }}>
                      <span className="text-sm font-bold" style={{ color: '#3B82F6' }}>{inviteAnalytics.otp_funnel.otp_verified}</span>
                    </div>
                    <p className="text-xs" style={{ color: 'rgba(229,231,235,0.6)' }}>Verified</p>
                  </div>
                  <div style={{ color: 'rgba(229,231,235,0.3)' }}>→</div>
                  
                  {/* Registered */}
                  <div className="flex-1 text-center">
                    <div className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.2)', border: '2px solid #22C55E' }}>
                      <span className="text-sm font-bold" style={{ color: '#22C55E' }}>{inviteAnalytics.otp_funnel.registered}</span>
                    </div>
                    <p className="text-xs" style={{ color: 'rgba(229,231,235,0.6)' }}>Registered</p>
                  </div>
                </div>
                
                {/* Drop-off Analysis */}
                {inviteAnalytics.dropoff_analysis && (
                  <div className="space-y-2 mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <p className="text-xs font-medium mb-3" style={{ color: 'rgba(229,231,235,0.7)' }}>Drop-off Points</p>
                    {Object.entries(inviteAnalytics.dropoff_analysis).map(([key, data]) => (
                      <div key={key} className="flex items-center justify-between text-xs">
                        <span style={{ color: 'rgba(229,231,235,0.5)' }}>{data.stage}</span>
                        <div className="flex items-center gap-2">
                          <span style={{ color: data.drop_rate > 50 ? '#EF4444' : data.drop_rate > 25 ? '#F59E0B' : '#22C55E' }}>
                            {data.drop_rate}% dropped
                          </span>
                          <span style={{ color: 'rgba(229,231,235,0.3)' }}>({data.from} → {data.to})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* OTP Stats Summary */}
                <div className="grid grid-cols-4 gap-2 mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="text-center p-2 rounded" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <p className="text-lg font-bold" style={{ color: '#7C3AED' }}>{inviteAnalytics.otp_funnel.verification_rate}%</p>
                    <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>OTP Success</p>
                  </div>
                  <div className="text-center p-2 rounded" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <p className="text-lg font-bold" style={{ color: '#22C55E' }}>{inviteAnalytics.otp_funnel.completion_rate}%</p>
                    <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>Completion</p>
                  </div>
                  <div className="text-center p-2 rounded" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <p className="text-lg font-bold" style={{ color: '#EF4444' }}>{inviteAnalytics.otp_funnel.blocked_max_attempts}</p>
                    <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>Blocked</p>
                  </div>
                  <div className="text-center p-2 rounded" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <p className="text-lg font-bold" style={{ color: '#F59E0B' }}>{inviteAnalytics.otp_stats?.expired_unused || 0}</p>
                    <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>OTP Expired</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={inviteFilter} onValueChange={setInviteFilter}>
          <SelectTrigger className="w-36 bg-white/5 border-white/10 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent style={{ background: '#0A0F2C', border: '1px solid rgba(255,255,255,0.1)' }}>
            <SelectItem value="all" className="text-white hover:bg-white/10">All Status</SelectItem>
            <SelectItem value="active" className="text-white hover:bg-white/10">Active</SelectItem>
            <SelectItem value="pending" className="text-white hover:bg-white/10">Pending</SelectItem>
            <SelectItem value="joined" className="text-white hover:bg-white/10">Joined</SelectItem>
            <SelectItem value="expired" className="text-white hover:bg-white/10">Expired</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder="Search inviter or recipient..."
          value={inviteSearch}
          onChange={(e) => setInviteSearch(e.target.value)}
          className="max-w-xs bg-white/5 border-white/10 text-white placeholder:text-gray-500"
        />
      </div>

      {/* Invites Table */}
      <Card className="border-0" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th className="text-left py-3 px-4" style={{ color: 'rgba(229,231,235,0.7)' }}>Inviter</th>
                  <th className="text-left py-3 px-4" style={{ color: 'rgba(229,231,235,0.7)' }}>Method</th>
                  <th className="text-left py-3 px-4" style={{ color: 'rgba(229,231,235,0.7)' }}>Recipient</th>
                  <th className="text-left py-3 px-4" style={{ color: 'rgba(229,231,235,0.7)' }}>Message</th>
                  <th className="text-left py-3 px-4" style={{ color: 'rgba(229,231,235,0.7)' }}>Status</th>
                  <th className="text-left py-3 px-4" style={{ color: 'rgba(229,231,235,0.7)' }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {invites.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-8 text-center" style={{ color: 'rgba(229,231,235,0.5)' }}>
                      No invitations found
                    </td>
                  </tr>
                ) : (
                  invites.map((invite) => (
                    <tr key={invite.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => {
                            // Find user from users list or fetch
                            const inviterUser = users.find(u => u.id === invite.inviter_id);
                            if (inviterUser) {
                              handleUserClick(inviterUser);
                            } else {
                              // Create minimal user object for click
                              handleUserClick({ id: invite.inviter_id, username: invite.inviter_username });
                            }
                          }}
                          className="flex items-center gap-1 hover:opacity-80 transition-opacity"
                          style={{ color: '#22D3EE' }}
                          data-testid={`inviter-link-${invite.id}`}
                        >
                          <span>@{invite.inviter_username || 'Unknown'}</span>
                          <ExternalLink className="h-3 w-3" />
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1.5">
                          {invite.invite_method === 'email' ? (
                            <Mail className="h-3.5 w-3.5" style={{ color: '#22D3EE' }} />
                          ) : invite.invite_method === 'sms' ? (
                            <Phone className="h-3.5 w-3.5" style={{ color: '#22C55E' }} />
                          ) : (
                            <Copy className="h-3.5 w-3.5" style={{ color: '#A78BFA' }} />
                          )}
                          <span style={{ color: 'rgba(229,231,235,0.7)', fontSize: '12px' }}>
                            {invite.invite_method === 'email' ? 'Email' : invite.invite_method === 'sms' ? 'SMS' : 'Copy'}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4" style={{ color: 'rgba(229,231,235,0.7)' }}>
                        {invite.recipient_email || invite.recipient_phone || '-'}
                      </td>
                      <td className="py-3 px-4" style={{ color: 'rgba(229,231,235,0.5)', maxWidth: '200px' }}>
                        <p className="truncate">{invite.message_preview || invite.message?.slice(0, 30) || '-'}</p>
                      </td>
                      <td className="py-3 px-4">
                        <Badge style={{ 
                          background: invite.converted ? 'rgba(34,197,94,0.2)' :
                                     invite.status === 'completed' ? 'rgba(34,197,94,0.2)' :
                                     invite.status === 'joined' ? 'rgba(34,197,94,0.2)' : 
                                     invite.status === 'opened' ? 'rgba(245,158,11,0.2)' : 'rgba(124,58,237,0.2)',
                          color: invite.converted ? '#22C55E' :
                                 invite.status === 'completed' ? '#22C55E' :
                                 invite.status === 'joined' ? '#22C55E' : 
                                 invite.status === 'opened' ? '#F59E0B' : '#A78BFA',
                          border: 'none',
                          fontSize: '11px'
                        }}>
                          {invite.converted ? 'Converted' :
                           invite.status === 'completed' ? 'Joined' :
                           invite.status === 'joined' ? 'Joined' : 
                           invite.status === 'opened' ? 'Opened' : 
                           invite.status || 'Sent'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4" style={{ color: 'rgba(229,231,235,0.5)', fontSize: '12px' }}>
                        {formatDate(invite.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0A0F2C 0%, #020617 100%)', overflowY: 'auto' }}>
      {/* Header */}
      <header className="sticky top-0 z-10 px-4 py-4" style={{ background: 'rgba(10,15,44,0.95)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8" style={{ color: '#7C3AED' }} />
            <div>
              <h1 className="text-xl font-bold" style={{ color: '#E5E7EB' }}>Privluxe Admin</h1>
              <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>Monitoring Dashboard</p>
            </div>
          </div>
          <Button onClick={logout} variant="ghost" className="text-gray-400 hover:text-white hover:bg-white/10">
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto p-4">
        {renderStats()}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'users', label: 'Users', icon: Users },
            { id: 'chats', label: 'Chats', icon: MessageSquare },
            { id: 'invites', label: 'Invites', icon: Send },
            { id: 'security', label: 'Security', icon: Shield },
          ].map((tab) => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              className={activeTab === tab.id ? 'text-white' : 'text-gray-400 hover:text-white hover:bg-white/10'}
              style={activeTab === tab.id ? { background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)' } : {}}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'users' && renderUsersList()}
        {activeTab === 'chats' && renderChatsList()}
        {activeTab === 'invites' && renderInvites()}
        
        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            {/* Password Change */}
            <Card style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: '#E5E7EB' }}>
                  <Key className="h-5 w-5" style={{ color: '#7C3AED' }} />
                  Password
                </CardTitle>
                <CardDescription style={{ color: 'rgba(229,231,235,0.5)' }}>
                  {securitySettings?.password_changed 
                    ? `Last changed: ${new Date(securitySettings.last_password_change).toLocaleDateString()}`
                    : 'Using default password - change it for security!'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!showPasswordChange ? (
                  <Button 
                    onClick={() => setShowPasswordChange(true)}
                    variant="outline"
                    className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    Change Password
                  </Button>
                ) : (
                  <div className="space-y-4 max-w-md">
                    <Input
                      type="password"
                      placeholder="Current Password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                    />
                    <Input
                      type="password"
                      placeholder="New Password (min 8 characters)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                    />
                    <Input
                      type="password"
                      placeholder="Confirm New Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                    />
                    <div className="flex gap-2">
                      <Button 
                        onClick={handlePasswordChange}
                        style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)' }}
                      >
                        Save Password
                      </Button>
                      <Button 
                        variant="ghost"
                        onClick={() => {
                          setShowPasswordChange(false);
                          setCurrentPassword('');
                          setNewPassword('');
                          setConfirmPassword('');
                        }}
                        className="text-gray-400 hover:text-white"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 2FA Setup */}
            <Card style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: '#E5E7EB' }}>
                  <Smartphone className="h-5 w-5" style={{ color: '#22C55E' }} />
                  Two-Factor Authentication (2FA)
                </CardTitle>
                <CardDescription style={{ color: 'rgba(229,231,235,0.5)' }}>
                  {securitySettings?.totp_enabled 
                    ? 'Enabled - Your account is protected with 2FA'
                    : 'Add an extra layer of security to your admin account'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {securitySettings?.totp_enabled ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: 'rgba(34,197,94,0.1)' }}>
                      <Shield className="h-5 w-5 text-green-500" />
                      <span className="text-green-400 text-sm">2FA is active</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="text"
                        placeholder="Enter 2FA code to disable"
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="bg-white/5 border-white/10 text-white max-w-[200px]"
                        maxLength={6}
                      />
                      <Button 
                        onClick={disable2FA}
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        Disable 2FA
                      </Button>
                    </div>
                  </div>
                ) : show2FASetup ? (
                  <div className="space-y-4 max-w-md">
                    <p className="text-sm" style={{ color: 'rgba(229,231,235,0.7)' }}>
                      Scan this QR code with Google Authenticator, Authy, or any TOTP app:
                    </p>
                    {qrCodeUrl && (
                      <div className="flex justify-center p-4 bg-white rounded-lg">
                        <img 
                          src={qrCodeUrl} 
                          alt="2FA QR Code" 
                          className="w-48 h-48"
                        />
                      </div>
                    )}
                    <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <p className="text-xs text-gray-400 mb-1">Manual entry key:</p>
                      <code className="text-sm text-purple-400 break-all">{totpSecret}</code>
                    </div>
                    <Input
                      type="text"
                      placeholder="Enter 6-digit code from app"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="bg-white/5 border-white/10 text-white"
                      maxLength={6}
                    />
                    <div className="flex gap-2">
                      <Button 
                        onClick={enable2FA}
                        style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' }}
                      >
                        Verify & Enable
                      </Button>
                      <Button 
                        variant="ghost"
                        onClick={() => {
                          setShow2FASetup(false);
                          setTotpCode('');
                          setTotpSecret(null);
                          setQrCodeUrl(null);
                        }}
                        className="text-gray-400 hover:text-white"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button 
                    onClick={setup2FA}
                    style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' }}
                  >
                    <Smartphone className="h-4 w-4 mr-2" />
                    Enable 2FA
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* IP Location Modal */}
      <Dialog open={showIPModal} onOpenChange={setShowIPModal}>
        <DialogContent 
          className="max-w-md border-0"
          style={{ 
            background: 'rgba(10, 15, 44, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#E5E7EB' }}>
              <MapPin className="h-5 w-5" style={{ color: '#22D3EE' }} />
              IP Location
            </DialogTitle>
            <DialogDescription style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
              {selectedIP}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            {ipLoading ? (
              <div className="text-center py-8" style={{ color: 'rgba(229,231,235,0.5)' }}>
                Loading location data...
              </div>
            ) : ipLocation?.error ? (
              <div className="text-center py-8" style={{ color: '#F59E0B' }}>
                {ipLocation.error}
              </div>
            ) : ipLocation ? (
              <div className="space-y-4">
                {/* Map Preview */}
                <div 
                  className="w-full h-48 rounded-xl overflow-hidden relative cursor-pointer group"
                  onClick={openGoogleMaps}
                  style={{ background: 'rgba(255,255,255,0.05)' }}
                >
                  <iframe
                    src={`https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d50000!2d${ipLocation.lon}!3d${ipLocation.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sin!4v1&output=embed`}
                    width="100%"
                    height="100%"
                    style={{ border: 0, pointerEvents: 'none' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-all">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 px-4 py-2 rounded-full" 
                         style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}>
                      <ExternalLink className="h-4 w-4 text-white" />
                      <span className="text-white text-sm font-medium">Open in Google Maps</span>
                    </div>
                  </div>
                </div>

                {/* Location Details */}
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <p className="text-xs mb-1" style={{ color: 'rgba(229,231,235,0.5)' }}>City</p>
                      <p className="font-medium" style={{ color: '#E5E7EB' }}>{ipLocation.city || 'Unknown'}</p>
                    </div>
                    <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <p className="text-xs mb-1" style={{ color: 'rgba(229,231,235,0.5)' }}>Region</p>
                      <p className="font-medium" style={{ color: '#E5E7EB' }}>{ipLocation.regionName || 'Unknown'}</p>
                    </div>
                    <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <p className="text-xs mb-1" style={{ color: 'rgba(229,231,235,0.5)' }}>Country</p>
                      <p className="font-medium" style={{ color: '#E5E7EB' }}>{ipLocation.country || 'Unknown'}</p>
                    </div>
                    <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <p className="text-xs mb-1" style={{ color: 'rgba(229,231,235,0.5)' }}>Timezone</p>
                      <p className="font-medium" style={{ color: '#E5E7EB' }}>{ipLocation.timezone || 'Unknown'}</p>
                    </div>
                  </div>
                  
                  <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <p className="text-xs mb-1" style={{ color: 'rgba(229,231,235,0.5)' }}>ISP / Organization</p>
                    <p className="font-medium text-sm" style={{ color: '#E5E7EB' }}>{ipLocation.isp || ipLocation.org || 'Unknown'}</p>
                  </div>

                  <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <p className="text-xs mb-1" style={{ color: 'rgba(229,231,235,0.5)' }}>Coordinates</p>
                    <p className="font-medium font-mono text-sm" style={{ color: '#22D3EE' }}>
                      {ipLocation.lat?.toFixed(4)}, {ipLocation.lon?.toFixed(4)}
                    </p>
                  </div>
                </div>

                <Button 
                  onClick={openGoogleMaps}
                  className="w-full text-white font-medium"
                  style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  Open in Google Maps
                </Button>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* User Profile Modal */}
      <Dialog open={showUserProfile} onOpenChange={setShowUserProfile}>
        <DialogContent 
          className="max-w-3xl max-h-[90vh] overflow-y-auto border-0"
          style={{ 
            background: 'rgba(10, 15, 44, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3" style={{ color: '#E5E7EB' }}>
              {userProfileData?.user?.profile_picture ? (
                <img 
                  src={userProfileData.user.profile_picture.startsWith('/') ? `${API.replace('/api', '')}${userProfileData.user.profile_picture}` : userProfileData.user.profile_picture} 
                  alt="Profile" 
                  className="w-12 h-12 rounded-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}>
                  <User className="h-6 w-6 text-white" />
                </div>
              )}
              <div>
                <span>{selectedUser?.username || 'User Profile'}</span>
                <p className="text-sm font-normal" style={{ color: 'rgba(229,231,235,0.5)' }}>
                  ID: {selectedUser?.id?.slice(0, 8)}...
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            {userProfileLoading ? (
              <div className="text-center py-8" style={{ color: 'rgba(229,231,235,0.5)' }}>
                Loading user profile...
              </div>
            ) : userProfileData ? (
              <div className="space-y-6">
                {/* User Stats */}
                <div className="grid grid-cols-5 gap-3">
                  {[
                    { label: 'Logins', value: userProfileData.stats?.login_count || 0, icon: Activity },
                    { label: 'Messages', value: userProfileData.stats?.message_count || 0, icon: MessageSquare },
                    { label: 'Calls', value: userProfileData.stats?.call_count || 0, icon: Phone },
                    { label: 'Media', value: userProfileData.stats?.media_shared_count || 0, icon: Video },
                    { label: 'Invites', value: userProfileData.stats?.invites_sent_count || 0, icon: Send },
                  ].map((stat) => (
                    <div key={stat.label} className="p-3 rounded-lg text-center" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <stat.icon className="h-5 w-5 mx-auto mb-1" style={{ color: '#7C3AED' }} />
                      <p className="text-xl font-bold" style={{ color: '#E5E7EB' }}>{stat.value}</p>
                      <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Profile Details */}
                <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#E5E7EB' }}>
                    <User className="h-4 w-4" style={{ color: '#7C3AED' }} />
                    Profile Details
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p style={{ color: 'rgba(229,231,235,0.5)' }}>Username</p>
                      <p style={{ color: '#E5E7EB' }}>{userProfileData.user?.username || '-'}</p>
                    </div>
                    <div>
                      <p style={{ color: 'rgba(229,231,235,0.5)' }}>Email</p>
                      <p style={{ color: '#E5E7EB' }}>{userProfileData.user?.email || '-'}</p>
                    </div>
                    <div>
                      <p style={{ color: 'rgba(229,231,235,0.5)' }}>Phone</p>
                      <p style={{ color: '#E5E7EB' }}>{userProfileData.user?.phone || '-'}</p>
                    </div>
                    <div>
                      <p style={{ color: 'rgba(229,231,235,0.5)' }}>Password Set</p>
                      <p style={{ color: userProfileData.user?.password_hash ? '#22C55E' : '#F59E0B' }}>
                        {userProfileData.user?.password_hash ? 'Yes ✓' : 'No'}
                      </p>
                    </div>
                    <div>
                      <p style={{ color: 'rgba(229,231,235,0.5)' }}>Monitoring Consent</p>
                      <p style={{ color: userProfileData.user?.agreed_to_monitoring ? '#22C55E' : '#EF4444' }}>
                        {userProfileData.user?.agreed_to_monitoring ? 'Accepted ✓' : 'Not Accepted'}
                      </p>
                    </div>
                    <div>
                      <p style={{ color: 'rgba(229,231,235,0.5)' }}>Member Since</p>
                      <p style={{ color: '#E5E7EB' }}>{formatDate(userProfileData.user?.created_at)}</p>
                    </div>
                    <div>
                      <p style={{ color: 'rgba(229,231,235,0.5)' }}>Last Login</p>
                      <p style={{ color: '#E5E7EB' }}>{formatDate(userProfileData.user?.last_login) || '-'}</p>
                    </div>
                    <div>
                      <p style={{ color: 'rgba(229,231,235,0.5)' }}>Last IP</p>
                      <p style={{ color: '#22D3EE' }}>{userProfileData.user?.ip_address || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Login History */}
                <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#E5E7EB' }}>
                    <Clock className="h-4 w-4" style={{ color: '#22D3EE' }} />
                    Login History
                  </h4>
                  {userProfileData.login_history?.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {userProfileData.login_history.map((login, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-center justify-between p-3 rounded-lg"
                          style={{ background: 'rgba(255,255,255,0.02)' }}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${login.success ? 'bg-green-500' : 'bg-red-500'}`} />
                            <div>
                              <p className="text-sm" style={{ color: '#E5E7EB' }}>
                                {login.device_type} • {login.browser} • {login.os}
                              </p>
                              <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>
                                {login.login_method} via {login.login_identifier}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <button
                              onClick={() => handleIPClick(login.ip_address)}
                              className="text-xs flex items-center gap-1 hover:underline"
                              style={{ color: '#22D3EE' }}
                            >
                              <MapPin className="h-3 w-3" />
                              {login.ip_address?.split(',')[0]?.slice(0, 15)}
                            </button>
                            <p className="text-xs" style={{ color: 'rgba(229,231,235,0.4)' }}>
                              {formatDate(login.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-4" style={{ color: 'rgba(229,231,235,0.5)' }}>
                      No login history recorded
                    </p>
                  )}
                </div>

                {/* Media Shared */}
                {userProfileData.media_shared?.length > 0 && (
                  <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#E5E7EB' }}>
                      <Video className="h-4 w-4" style={{ color: '#F59E0B' }} />
                      Media Shared ({userProfileData.media_shared.length})
                    </h4>
                    <div className="grid grid-cols-4 gap-2">
                      {userProfileData.media_shared.slice(0, 8).map((media, idx) => (
                        <div 
                          key={idx} 
                          className="aspect-square rounded-lg overflow-hidden"
                          style={{ background: 'rgba(255,255,255,0.05)' }}
                        >
                          {media.message_type === 'image' && media.media_urls?.[0] ? (
                            <img 
                              src={getSecureMediaUrl(media.media_urls[0])} 
                              alt="Media" 
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Video className="h-6 w-6" style={{ color: 'rgba(229,231,235,0.3)' }} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Invites Sent */}
                {userProfileData.invites_sent?.length > 0 && (
                  <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#E5E7EB' }}>
                      <Send className="h-4 w-4" style={{ color: '#EC4899' }} />
                      Invites Sent ({userProfileData.invites_sent.length})
                    </h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {userProfileData.invites_sent.map((invite, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-center justify-between p-3 rounded-lg"
                          style={{ background: 'rgba(255,255,255,0.02)' }}
                        >
                          <div className="flex items-center gap-3">
                            {invite.invite_method === 'email' ? (
                              <Mail className="h-4 w-4" style={{ color: '#22D3EE' }} />
                            ) : invite.invite_method === 'sms' ? (
                              <Phone className="h-4 w-4" style={{ color: '#22C55E' }} />
                            ) : (
                              <Copy className="h-4 w-4" style={{ color: '#A78BFA' }} />
                            )}
                            <div>
                              <p className="text-sm" style={{ color: '#E5E7EB' }}>
                                {invite.recipient_email || invite.recipient_phone || 'Link copied'}
                              </p>
                              <p className="text-xs" style={{ color: 'rgba(229,231,235,0.5)' }}>
                                {invite.invite_method === 'email' ? 'Email' : invite.invite_method === 'sms' ? 'SMS' : 'Copy Link'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge style={{ 
                              background: invite.converted ? 'rgba(34,197,94,0.2)' : 
                                         invite.status === 'completed' ? 'rgba(34,197,94,0.2)' :
                                         invite.status === 'accepted' ? 'rgba(34,211,238,0.2)' :
                                         invite.status === 'opened' ? 'rgba(245,158,11,0.2)' : 'rgba(124,58,237,0.2)',
                              color: invite.converted ? '#22C55E' : 
                                     invite.status === 'completed' ? '#22C55E' :
                                     invite.status === 'accepted' ? '#22D3EE' :
                                     invite.status === 'opened' ? '#F59E0B' : '#A78BFA',
                              border: 'none',
                              fontSize: '10px'
                            }}>
                              {invite.converted ? 'Converted' : 
                               invite.status === 'completed' ? 'Joined' :
                               invite.status === 'opened' ? 'Opened' :
                               invite.status || 'Sent'}
                            </Badge>
                            <p className="text-xs mt-1" style={{ color: 'rgba(229,231,235,0.4)' }}>
                              {formatDate(invite.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
            
            {/* Delete User Button */}
            {userProfileData && (
              <div className="mt-6 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => setShowDeleteUserConfirm(true)}
                  style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#EF4444', border: '1px solid rgba(239, 68, 68, 0.3)' }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete User Permanently
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Modal */}
      <Dialog open={showDeleteUserConfirm} onOpenChange={setShowDeleteUserConfirm}>
        <DialogContent 
          className="max-w-md border-0"
          style={{ 
            background: 'rgba(10, 15, 44, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(239, 68, 68, 0.3)'
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#EF4444' }}>
              <AlertTriangle className="h-5 w-5" />
              Delete User Permanently
            </DialogTitle>
            <DialogDescription style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
              Are you sure you want to delete <strong style={{ color: '#E5E7EB' }}>{selectedUser?.username}</strong>?
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 p-4 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
            <p className="text-sm mb-3" style={{ color: 'rgba(229, 231, 235, 0.8)' }}>
              This action will permanently delete:
            </p>
            <ul className="text-sm space-y-1" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
              <li>• User account and profile</li>
              <li>• All messages sent/received</li>
              <li>• All conversations</li>
              <li>• All contacts and friend requests</li>
              <li>• All invites sent</li>
              <li>• All call records</li>
              <li>• Login history</li>
            </ul>
            <p className="text-sm mt-3 font-semibold" style={{ color: '#EF4444' }}>
              This action cannot be undone!
            </p>
          </div>
          
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowDeleteUserConfirm(false)}
              disabled={deletingUser}
              style={{ borderColor: 'rgba(255,255,255,0.2)', color: '#E5E7EB' }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={handleDeleteUser}
              disabled={deletingUser}
              style={{ background: '#EF4444' }}
            >
              {deletingUser ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete User
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recording Playback Modal */}
      <Dialog open={showRecordingModal} onOpenChange={setShowRecordingModal}>
        <DialogContent 
          className="max-w-md border-0"
          style={{ 
            background: 'rgba(10, 15, 44, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#E5E7EB' }}>
              {selectedRecording?.type === 'audio' ? (
                <PhoneCall className="h-5 w-5" style={{ color: '#22D3EE' }} />
              ) : (
                <Video className="h-5 w-5" style={{ color: '#22D3EE' }} />
              )}
              Call Recording
            </DialogTitle>
            <DialogDescription style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
              {selectedRecording?.caller} → {selectedRecording?.receiver} • {formatDuration(selectedRecording?.duration)}
              {selectedRecording?.userRole && (
                <span className="ml-2 px-2 py-0.5 rounded text-xs" style={{ 
                  background: selectedRecording.userRole === 'Caller' ? 'rgba(34,211,238,0.2)' : 'rgba(124,58,237,0.2)',
                  color: selectedRecording.userRole === 'Caller' ? '#22D3EE' : '#A78BFA'
                }}>
                  {selectedRecording.userRole}'s perspective
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            {recordingError && !mediaLoaded ? (
              <div className="p-6 text-center rounded-xl" style={{ background: 'rgba(239,68,68,0.1)' }}>
                <AlertTriangle className="h-8 w-8 mx-auto mb-2" style={{ color: '#EF4444' }} />
                <p style={{ color: '#EF4444' }}>Recording not available</p>
                <p className="text-xs mt-1" style={{ color: 'rgba(229,231,235,0.5)' }}>
                  The recording file may have been deleted or is unavailable
                </p>
                {/* Fallback download link */}
                <a 
                  href={selectedRecording?.url} 
                  download 
                  className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-lg text-sm"
                  style={{ background: 'rgba(34,211,238,0.2)', color: '#22D3EE' }}
                >
                  <Download className="h-4 w-4" />
                  Try downloading instead
                </a>
              </div>
            ) : (
              <div className="space-y-4">
                {selectedRecording?.type === 'video' ? (
                  <div className="space-y-2">
                    <video 
                      src={selectedRecording?.url}
                      controls
                      autoPlay
                      playsInline
                      preload="auto"
                      className="w-full rounded-xl"
                      style={{ maxHeight: '400px', background: '#000' }}
                      onCanPlay={() => setMediaLoaded(true)}
                      onLoadedMetadata={() => setMediaLoaded(true)}
                      onError={(e) => {
                        // Only set error if media hasn't successfully loaded yet
                        if (!mediaLoaded) {
                          console.error('Video load error:', e.target.error);
                          setRecordingError(true);
                        }
                      }}
                    />
                    {/* Download button and Safari note */}
                    <div className="flex flex-col items-center gap-2">
                      <a 
                        href={selectedRecording?.url} 
                        download={`call_${selectedRecording?.callId || 'recording'}.webm`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition hover:bg-cyan-500/20"
                        style={{ background: 'rgba(34,211,238,0.1)', color: '#22D3EE' }}
                      >
                        <Download className="h-4 w-4" />
                        Download Video
                      </a>
                      <p className="text-xs text-center" style={{ color: 'rgba(229,231,235,0.4)' }}>
                        If video doesn't play, download and open in VLC or Chrome
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <audio 
                      src={selectedRecording?.url}
                      controls
                      className="w-full"
                      onCanPlay={() => setMediaLoaded(true)}
                      onLoadedMetadata={() => setMediaLoaded(true)}
                      onError={(e) => {
                        // Only set error if media hasn't successfully loaded yet
                        if (!mediaLoaded) {
                          console.error('Audio load error:', e.target.error);
                          setRecordingError(true);
                        }
                      }}
                    />
                    {/* Always show download option for audio */}
                    <div className="flex justify-center mt-3">
                      <a 
                        href={selectedRecording?.url} 
                        download 
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition hover:bg-white/10"
                        style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(229,231,235,0.6)' }}
                      >
                        <Download className="h-3 w-3" />
                        Download
                      </a>
                    </div>
                  </div>
                )}
                <p className="text-xs text-center" style={{ color: 'rgba(229,231,235,0.4)' }}>
                  {formatDate(selectedRecording?.date)}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* SMS Dialog */}
      <Dialog open={showSMSDialog} onOpenChange={setShowSMSDialog}>
        <DialogContent 
          className="max-w-lg border-0"
          style={{ 
            background: 'rgba(10, 15, 44, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#E5E7EB' }}>
              <Send className="h-5 w-5" style={{ color: '#A78BFA' }} />
              Send SMS to {smsTargetUser?.username || smsTargetUser?.phone}
            </DialogTitle>
            <DialogDescription style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
              Phone: {smsTargetUser?.phone}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block" style={{ color: 'rgba(229,231,235,0.7)' }}>
                Message
              </label>
              <textarea
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                rows={8}
                className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                style={{ 
                  background: 'rgba(255,255,255,0.05)', 
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#E5E7EB'
                }}
                placeholder="Enter your message..."
              />
              <p className="text-xs mt-1" style={{ color: 'rgba(229,231,235,0.4)' }}>
                {smsMessage.length} characters
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setShowSMSDialog(false);
                setSmsTargetUser(null);
                setSmsMessage('');
              }}
              style={{ 
                background: 'transparent', 
                borderColor: 'rgba(255,255,255,0.1)',
                color: '#E5E7EB'
              }}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={() => setShowSMSConfirm(true)}
              disabled={!smsMessage.trim() || sendingSMS}
              style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)' }}
            >
              <Send className="h-4 w-4 mr-2" />
              Send SMS
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SMS Confirmation Dialog */}
      <Dialog open={showSMSConfirm} onOpenChange={setShowSMSConfirm}>
        <DialogContent 
          className="max-w-sm border-0"
          style={{ 
            background: 'rgba(10, 15, 44, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#E5E7EB' }}>
              <AlertTriangle className="h-5 w-5" style={{ color: '#F59E0B' }} />
              Confirm Send SMS
            </DialogTitle>
            <DialogDescription style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
              Are you sure you want to send this SMS to <strong style={{ color: '#E5E7EB' }}>{smsTargetUser?.username || smsTargetUser?.phone}</strong>?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div 
              className="p-3 rounded-lg text-sm"
              style={{ 
                background: 'rgba(255,255,255,0.03)', 
                border: '1px solid rgba(255,255,255,0.05)',
                color: 'rgba(229,231,235,0.7)'
              }}
            >
              {smsMessage.length > 100 ? smsMessage.slice(0, 100) + '...' : smsMessage}
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowSMSConfirm(false)}
              style={{ 
                background: 'transparent', 
                borderColor: 'rgba(255,255,255,0.1)',
                color: '#E5E7EB'
              }}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={sendSMSToUser}
              disabled={sendingSMS}
              style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)' }}
            >
              {sendingSMS ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Confirm Send
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
